import React, { useMemo } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { LegDetail } from 'src/generated/services/TFinancialApi';
import { CO2, Ton } from 'src/presentations/components/atom/unit';
import truckIcon from 'src/assets/images/icons/truck.svg';
import shipIcon from 'src/assets/images/icons/ship.svg';
import aeroplaneIcon from 'src/assets/images/icons/airplane.svg';
import { SimpleFormatNumber, displayValueOrFallback } from 'src/utils/utility';
import { Link } from 'react-router-dom';

type LegCardProps = {
  legDetail: LegDetail;
  index: number;
  invoiceId: string;
};

const transportToIcon = (transportMedium: LegDetail['transportMedium']) => {
  switch (transportMedium) {
    case 'ROAD':
      return truckIcon;
    case 'SEA':
      return shipIcon;
    case 'AIR':
      return aeroplaneIcon;
    default:
      return truckIcon;
  }
};

const LegCard: React.FC<LegCardProps> = ({ legDetail, index, invoiceId }) => {
  const icon = useMemo(() => transportToIcon(legDetail?.transportMedium), [legDetail]);
  return (
    <Box data-testid='leg-container' className='bg-white rounded-xl border-1 border-neutral-20 pt-12 pb-8  mt-10'>
      <Box>
        <Box className='border-neutral-20 border-b-1 px-12 pb-12'>
          <Box className='flex justify-between'>
            <Box className='bg-primary/20 px-10 py-4 rounded-full flex gap-10 items-center'>
              <h3 className='text-xs font-bold text-primary'>{`LEG ${index + 1}`}</h3>
              <Divider sx={{ my: '0.25rem' }} className='bg-green-50 mt-1 mb-1' orientation='vertical' variant='middle' flexItem />
              <h4 className='text-xs font-bold text-primary'>{displayValueOrFallback(legDetail?.legCurrency)}</h4>
            </Box>
            <img src={icon} alt='FrameIconGroup' />
          </Box>
          <Box className='bg-neutral-10 mt-12 rounded-lg text-center border-1 py-2 border-neutral-20 flex items-center justify-center'>
            <p className='font-normal text-grey-10 text-xs'>{displayValueOrFallback(legDetail?.legCurrency, 'No Data')} 1 =</p>
            <p className='font-bold  text-grey-10 text-xs'>
              &nbsp;{displayValueOrFallback(legDetail?.paymentCurrency)} {displayValueOrFallback(legDetail?.paymentCurrencyExchangeRate)}
            </p>
          </Box>
        </Box>
        <Box className='p-12'>
          <Box className='border-neutral-20 border-b-1 pb-5'>
            <Box className='flex justify-between items-center mb-6'>
              <h3 className='text-12 xl:text-16 text-blue_grey-50 font-bold text-sm leading-18'>
                Total Amount <span className='text-10 xl:text-12 text-blue_grey-50 font-normal block'>Before Audit</span>
              </h3>
              <Typography className='!text-10 xl:!text-12 text-grey-40'>After Tax</Typography>
            </Box>
            <Box className='flex justify-between items-center'>
              <Typography className='!text-10 xl:!text-12'>{displayValueOrFallback(legDetail?.legCurrency)}</Typography>
              <Typography className='!text-12 xl:!text-16' fontSize='1rem' fontWeight='bold'>
                {displayValueOrFallback(legDetail?.legAmountAfterTax)}
              </Typography>
            </Box>
            <Box className='flex justify-between items-center'>
              <Typography className='!text-10 xl:!text-12 text-grey-40' fontSize='0.625rem'>
                {displayValueOrFallback(legDetail?.paymentCurrency)}
              </Typography>
              <Typography className='!text-12 xl:!text-16 text-grey-40' fontWeight='bold' fontSize='0.75rem'>
                {displayValueOrFallback(legDetail?.paymentAmountAfterTax)}
              </Typography>
            </Box>
          </Box>

          <Box className='border-neutral-20 border-b-1 py-12 h-93'>
            <Box className='flex justify-between items-center mb-6'>
              <h3 className='text-12 xl:text-16 text-blue_grey-50 font-bold text-sm'>Total Deviations</h3>
              <Typography className='!text-10 xl:!text-12 text-grey-40' fontSize='0.625rem'>
                After Tax
              </Typography>
            </Box>
            <Box className='flex justify-between items-center'>
              <Typography className='!text-10 xl:!text-12'>{displayValueOrFallback(legDetail?.legCurrency)}</Typography>
              <Typography className='!text-12 xl:!text-16 text-red-40' fontSize='1rem' fontWeight='bold'>
                {displayValueOrFallback(legDetail?.legDeviationAmountAfterTax)}
              </Typography>
            </Box>
            <Box className='flex justify-between items-center'>
              <Typography className='!text-10 xl:!text-12 text-grey-40'>{displayValueOrFallback(legDetail?.paymentCurrency)}</Typography>
              <Typography className='!text-12 xl:!text-16 text-red-40 text-grey-40' fontWeight='bold'>
                {displayValueOrFallback(legDetail?.paymentDeviationAmountAfterTax)}
              </Typography>
            </Box>
          </Box>

          <Box className='border-neutral-20 border-b-1 py-12 h-75'>
            <Box className='flex justify-between items-center'>
              <h3 className='text-12 xl:text-16 text-blue_grey-50 font-bold text-sm'>
                <CO2 /> Emissions
              </h3>
            </Box>
            <Box className='flex justify-between items-center'>
              <Typography className='!text-12 xl:!text-16 !font-bold' sx={{ marginTop: '6px' }}>
                {displayValueOrFallback(legDetail?.co2Emission)} {legDetail?.co2Emission && <Ton />}
              </Typography>
            </Box>
          </Box>

          <Box className='border-neutral-20 border-b-0 py-12'>
            <Box className='flex justify-between items-center'>
              <h3 className='text-12 xl:text-16 text-blue_grey-50 font-bold text-sm'>Invoices</h3>
            </Box>
            <Box className='flex justify-between mt-10'>
              <h3 className='!text-12 xl:!text-16 !font-bold'>{displayValueOrFallback(SimpleFormatNumber(legDetail?.invoices?.length))}</h3>
              <Box className='flex flex-col'>
                {legDetail?.invoices
                  ? legDetail?.invoices.map((invoice, index) => (
                      <Link
                        target='_blank'
                        to={`/action-details/${invoice.id}`}
                        key={invoice.invoiceNumber}
                        className={
                          `${invoice.id !== invoiceId ? 'text-xs text-grey-40 ' : 'text-xs text-primary font-bold'}` +
                          `${index !== 0 ? 'border-neutral-40 border-t-1 py-2' : 'py-2'}`
                        }
                      >
                        {displayValueOrFallback(invoice.invoiceNumber)}
                      </Link>
                    ))
                  : null}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LegCard;
